import React, { useState, useEffect } from 'react';
import { checkAuth, getUsers, logout as ssoLogout, setCurrentUser } from './services/api';
import { Header } from './components/Header';
import Feed from './pages/Feed';
import ManagerDashboard from './pages/ManagerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import About from './pages/About';
import UserSelect from './components/UserSelect';
import { LanguageProvider } from './contexts/LanguageContext';
import './index.css';

function App() {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState('feed');

  useEffect(() => {
    // Check URL for error params from auth callback
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('error');
    if (authError) {
      setError(authError);
      // Clean the URL
      window.history.replaceState({}, '', '/');
    }

    checkSession();
  }, []);

  async function checkSession() {
    try {
      const response = await checkAuth();
      if (response.data) {
        setUser(response.data);
        await loadAllUsers();
      }
    } catch (err) {
      console.error('Session check failed:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadAllUsers() {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }

  function handleLogout() {
    setCurrentUser(null);
    setUser(null);
    setUsers([]);
    setCurrentPage('feed');
    ssoLogout();
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading K2 Kudos...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <UserSelect error={error} />;
  }

  const isManager = user.is_manager ?? users.some(u => u.manager_id === user.id);
  const userWithManager = { ...user, is_manager: isManager };

  function renderPage() {
    switch (currentPage) {
      case 'manager':
        return <ManagerDashboard user={userWithManager} />;
      case 'admin':
        return <AdminDashboard user={userWithManager} />;
      case 'profile':
        return <Profile user={userWithManager} />;
      case 'about':
        return <About />;
      default:
        return <Feed user={userWithManager} users={users} onUserUpdate={checkSession} />;
    }
  }

  return (
    <LanguageProvider>
      <div className="min-h-screen bg-background">
        <Header
          user={userWithManager}
          users={users}
          currentPage={currentPage}
          onNavigate={setCurrentPage}
          onLogout={handleLogout}
        />
        <main className="mx-auto max-w-7xl px-4 py-6 lg:px-8">
          {renderPage()}
        </main>
      </div>
    </LanguageProvider>
  );
}

export default App;
